import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { RotateLeft, RotateRight, TaskAlt } from "@mui/icons-material";
import * as pdfjs from "pdfjs-dist";
// eslint-disable-next-line import/no-unresolved
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import {
  useApplyRotation,
  useViewVersion,
} from "@/api/hooks/documents";
import type {
  DocumentValidation,
  DocumentVersion,
  PageRotationPayload,
} from "@/api/types";

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
// pdfjs v5 decodes JBig2/CCITTFax/OpenJPEG image streams via WASM and
// needs ``wasmUrl`` passed to ``getDocument`` — without it the worker
// fails on most scanned PDFs and leaves the canvas blank. The
// ``pdfjsWasm`` plugin in vite.config exposes the wasm assets here.
const WASM_URL = "/pdfjs-wasm/";

type Rotation = 0 | 90 | 180 | 270;

interface Props {
  documentId: string;
  versionId: string;
  /** Initial NEEDS_REVIEW row from the orientation validator. Its
   * extracted_value.pages list seeds the per-page rotation choices. */
  validation: DocumentValidation;
  /** Fired after a successful apply-rotation. Receives the new (v2)
   * version and the content validators that re-ran on it so the
   * parent can pivot straight into the next review panel (delegation
   * log, GCP, …) without closing the drawer. */
  onApplied: (newVersion: DocumentVersion, validations: DocumentValidation[]) => void;
  onCancel: () => void;
}

interface PageProposal {
  page_number: number;
  /** Rotation Bedrock proposed (clockwise degrees to make page upright). */
  proposed: Rotation;
}

function readProposals(v: DocumentValidation): PageProposal[] {
  const extracted = v.extracted_value as
    | { pages?: Array<{ page_number?: number; clockwise_to_correct?: number }> }
    | null;
  const pages = extracted?.pages ?? [];
  return pages
    .map((p) => ({
      page_number: Number(p.page_number),
      proposed: (p.clockwise_to_correct as Rotation) ?? 0,
    }))
    .filter((p) => Number.isFinite(p.page_number));
}

/**
 * PDF.js renderer that draws each page on a canvas with a chosen
 * rotation applied (via viewport.transform). Each page gets its own
 * canvas + a rotation control. The operator can override what Bedrock
 * proposed before confirming.
 */
export function RotationReviewPanel({
  documentId,
  versionId,
  validation,
  onApplied,
  onCancel,
}: Props) {
  const view = useViewVersion();
  const apply = useApplyRotation(documentId);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Per-page chosen rotations, keyed by 1-based page number.
  const [rotations, setRotations] = useState<Record<number, Rotation>>(() => {
    const init: Record<number, Rotation> = {};
    for (const p of readProposals(validation)) {
      init[p.page_number] = p.proposed;
    }
    return init;
  });

  const [pages, setPages] = useState<
    { pageNumber: number; width: number; height: number }[]
  >([]);

  // Bedrock sometimes returns proposals for only some pages of a
  // multi-page scan — typical "the LLM analyzed page 1 and stopped"
  // failure mode. Real multi-page scans are almost always uniformly
  // rotated, so once pdfjs reports the actual page count, fill any
  // missing entry with the most common non-zero proposed rotation.
  // Pages Bedrock explicitly marked 0 stay 0 (we trust the explicit
  // signal).
  useEffect(() => {
    if (pages.length === 0) return;
    const proposals = readProposals(validation);
    const counts: Record<number, number> = {};
    for (const p of proposals) {
      if (p.proposed !== 0) counts[p.proposed] = (counts[p.proposed] ?? 0) + 1;
    }
    let mode: Rotation = 0;
    let best = 0;
    for (const [rot, n] of Object.entries(counts)) {
      if (n > best) {
        best = n;
        mode = Number(rot) as Rotation;
      }
    }
    if (mode === 0) return;
    setRotations((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const p of pages) {
        if (next[p.pageNumber] === undefined) {
          next[p.pageNumber] = mode;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [pages, validation]);

  // Fetch the presigned URL once. The view mutation also writes a
  // document_version_viewed audit event — useful evidence that the
  // operator actually looked at the file before approving rotation.
  useEffect(() => {
    let active = true;
    setError(null);
    view
      .mutateAsync({ documentId, versionId })
      .then((res) => {
        if (active) setUrl(res.download_url);
      })
      .catch((e) => {
        if (active) setError(e instanceof Error ? e.message : "Could not open PDF");
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, versionId]);

  // Load page dimensions once we have the URL. Canvas rendering is
  // re-triggered every time rotations change so the operator sees an
  // immediate preview of their choices.
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    (async () => {
      try {
        const doc = await pdfjs.getDocument({ url, wasmUrl: WASM_URL }).promise;
        const collected: { pageNumber: number; width: number; height: number }[] = [];
        for (let i = 1; i <= doc.numPages; i++) {
          const page = await doc.getPage(i);
          const vp = page.getViewport({ scale: 1 });
          collected.push({ pageNumber: i, width: vp.width, height: vp.height });
        }
        if (!cancelled) setPages(collected);
      } catch (e) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Failed to load PDF");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  // Render every page to its canvas whenever rotations change. PDF.js
  // applies rotation natively via the viewport options — no manual
  // canvas transform needed.
  useEffect(() => {
    if (!url || pages.length === 0) return;
    let cancelled = false;
    (async () => {
      const doc = await pdfjs.getDocument({ url, wasmUrl: WASM_URL }).promise;
      for (const p of pages) {
        if (cancelled) return;
        const page = await doc.getPage(p.pageNumber);
        const rot = rotations[p.pageNumber] ?? 0;
        const SCALE = 1.0;
        const viewport = page.getViewport({ scale: SCALE, rotation: rot });
        const canvas = document.querySelector<HTMLCanvasElement>(
          `canvas[data-rot-page="${p.pageNumber}"]`,
        );
        if (!canvas) continue;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = viewport.width * dpr;
        canvas.height = viewport.height * dpr;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        const ctx = canvas.getContext("2d");
        if (!ctx) continue;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        try {
          // pdfjs v5 RenderParameters wants both ``canvas`` and
          // ``canvasContext``. Older pdfjs versions only needed the
          // context; passing both is backwards-compatible.
          await page.render({
            canvas,
            canvasContext: ctx,
            viewport,
          }).promise;
        } catch {
          // benign: a later effect cancelled this render
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url, pages, rotations]);

  const setRotation = (pageNumber: number, value: Rotation) => {
    setRotations((r) => ({ ...r, [pageNumber]: value }));
  };

  const cycleRotation = (pageNumber: number, direction: -90 | 90) => {
    const current = rotations[pageNumber] ?? 0;
    const next = (((current + direction) % 360) + 360) % 360;
    setRotations((r) => ({ ...r, [pageNumber]: next as Rotation }));
  };

  const handleApply = async () => {
    setSubmitError(null);
    const payload: PageRotationPayload[] = pages.map((p) => ({
      page_number: p.pageNumber,
      clockwise_degrees: (rotations[p.pageNumber] ?? 0) as 0 | 90 | 180 | 270,
    }));
    try {
      const res = await apply.mutateAsync({
        validationId: validation.id,
        payload: { rotations: payload },
      });
      onApplied(res.new_version, res.validations);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "Failed to apply rotation");
    }
  };

  const allZero = pages.every((p) => (rotations[p.pageNumber] ?? 0) === 0);

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        Bedrock detected that one or more pages look rotated. The chosen
        rotation is previewed below — adjust any page if needed, then
        Confirm. A corrected copy is saved as a new version; the
        original is preserved for the audit trail.
      </Alert>
      {error && <Alert severity="error">{error}</Alert>}
      {!url && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {url && pages.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      <Stack spacing={3}>
        {pages.map((p) => {
          const r = rotations[p.pageNumber] ?? 0;
          const proposed = readProposals(validation).find(
            (x) => x.page_number === p.pageNumber,
          )?.proposed;
          return (
            <Box
              key={p.pageNumber}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Page {p.pageNumber}
                  {proposed !== undefined && proposed !== 0 && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      (Bedrock proposed {proposed}° clockwise)
                    </Typography>
                  )}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Tooltip title="Rotate 90° counter-clockwise">
                    <IconButton
                      size="small"
                      onClick={() => cycleRotation(p.pageNumber, -90)}
                      disabled={apply.isPending}
                    >
                      <RotateLeft fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={r}
                    onChange={(_, v) => {
                      if (v !== null) setRotation(p.pageNumber, v as Rotation);
                    }}
                    disabled={apply.isPending}
                  >
                    <ToggleButton value={0}>0°</ToggleButton>
                    <ToggleButton value={90}>90°</ToggleButton>
                    <ToggleButton value={180}>180°</ToggleButton>
                    <ToggleButton value={270}>270°</ToggleButton>
                  </ToggleButtonGroup>
                  <Tooltip title="Rotate 90° clockwise">
                    <IconButton
                      size="small"
                      onClick={() => cycleRotation(p.pageNumber, 90)}
                      disabled={apply.isPending}
                    >
                      <RotateRight fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  bgcolor: "background.default",
                  p: 1,
                }}
              >
                <canvas data-rot-page={p.pageNumber} />
              </Box>
            </Box>
          );
        })}
      </Stack>

      {submitError && <Alert severity="error">{submitError}</Alert>}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button onClick={onCancel} disabled={apply.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={apply.isPending || pages.length === 0}
          startIcon={
            apply.isPending ? <CircularProgress size={14} /> : <TaskAlt fontSize="small" />
          }
        >
          {apply.isPending
            ? "Applying…"
            : allZero
              ? "Confirm (no rotation)"
              : "Confirm rotation"}
        </Button>
      </Box>
    </Stack>
  );
}
